import React, { useEffect, useRef, useState } from 'react';
import './css/contact-container.css';
import {
	CAPTCHA_CONFIG_ERROR,
	CONTACT_API_URL,
	initialFormData,
	RECAPTCHA_ACTION,
	RECAPTCHA_SCRIPT_ID,
	RECAPTCHA_SITE_KEY,
	RECAPTCHA_TYPE,
	validateForm,
} from './contact-form-utils';

function buildPackageMessage(selectedPackage) {
	if (!selectedPackage) {
		return '';
	}

	const priceLine = selectedPackage.price ? ` Package fee: ${selectedPackage.price}.` : '';

	return `I would like to request the ${selectedPackage.name} package under ${selectedPackage.label}.${priceLine} Please contact me with the next steps.`;
}

export default function ContactFormSection({
	className = '',
	title = 'Send us a Message',
	selectedPackage = null,
	compact = false,
}) {
	const [formData, setFormData] = useState(initialFormData);
	const [files, setFiles] = useState({ nicFront: null, nicBack: null });
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState({ type: 'idle', message: '' });
	const [captchaToken, setCaptchaToken] = useState('');
	const [captchaError, setCaptchaError] = useState(RECAPTCHA_SITE_KEY ? '' : CAPTCHA_CONFIG_ERROR);
	const [isCaptchaReady, setIsCaptchaReady] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const captchaContainerRef = useRef(null);
	const captchaWidgetIdRef = useRef(null);

	useEffect(() => {
		if (!selectedPackage) {
			return;
		}

		setFormData((current) => ({
			...current,
			serviceType: selectedPackage.name,
			message: buildPackageMessage(selectedPackage),
		}));
		setFiles({ nicFront: null, nicBack: null });
		setErrors((current) => ({
			...current,
			serviceType: undefined,
			message: undefined,
			nicFront: undefined,
			nicBack: undefined,
		}));
		setStatus({ type: 'idle', message: '' });
	}, [selectedPackage]);

	useEffect(() => {
		if (!RECAPTCHA_SITE_KEY) {
			return undefined;
		}

		const renderCaptcha = () => {
			if (RECAPTCHA_TYPE === 'v3') {
				setIsCaptchaReady(true);
				return;
			}

			if (!window.grecaptcha || !captchaContainerRef.current || captchaWidgetIdRef.current !== null) {
				return;
			}

			captchaWidgetIdRef.current = window.grecaptcha.render(captchaContainerRef.current, {
				sitekey: RECAPTCHA_SITE_KEY,
				callback: (token) => {
					setCaptchaToken(token);
					setCaptchaError('');
				},
				'expired-callback': () => {
					setCaptchaToken('');
					setCaptchaError('reCAPTCHA expired. Please verify again.');
				},
				'error-callback': () => {
					setCaptchaToken('');
					setCaptchaError('reCAPTCHA could not be verified. Refresh the page and try again.');
				},
			});

			setIsCaptchaReady(true);
		};

		if (window.grecaptcha) {
			window.grecaptcha.ready(renderCaptcha);
			return undefined;
		}

		let script = document.getElementById(RECAPTCHA_SCRIPT_ID);
		const handleLoad = () => {
			if (window.grecaptcha) {
				window.grecaptcha.ready(renderCaptcha);
			}
		};
		const handleError = () => {
			setCaptchaError('reCAPTCHA failed to load. Check your network connection and try again.');
		};

		if (!script) {
			script = document.createElement('script');
			script.id = RECAPTCHA_SCRIPT_ID;
			script.src =
				RECAPTCHA_TYPE === 'v3'
					? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`
					: 'https://www.google.com/recaptcha/api.js?render=explicit';
			script.async = true;
			script.defer = true;
			script.addEventListener('load', handleLoad);
			script.addEventListener('error', handleError);
			document.body.appendChild(script);
		} else {
			script.addEventListener('load', handleLoad);
			script.addEventListener('error', handleError);
		}

		return () => {
			if (script) {
				script.removeEventListener('load', handleLoad);
				script.removeEventListener('error', handleError);
			}
		};
	}, []);

	const handleChange = (event) => {
		const { name, value } = event.target;

		setFormData((current) => ({
			...current,
			[name]: value,
		}));

		setErrors((current) => {
			if (!current[name]) {
				return current;
			}

			const nextErrors = validateForm({
				...formData,
				[name]: value,
			}, {
				compact,
				requiresFiles: compact,
				files,
			});

			return nextErrors;
		});
	};

	const handleBlur = (event) => {
		const { name, value } = event.target;
		const nextErrors = validateForm({
			...formData,
			[name]: value,
		}, {
			compact,
			requiresFiles: compact,
			files,
		});

		setErrors((current) => ({
			...current,
			[name]: nextErrors[name],
		}));
	};

	const handleFileChange = (event) => {
		const { name, files: selectedFiles } = event.target;
		const nextFile = selectedFiles && selectedFiles.length > 0 ? selectedFiles[0] : null;

		setFiles((current) => ({
			...current,
			[name]: nextFile,
		}));

		setErrors((current) => {
			if (!current[name]) {
				return current;
			}

			const nextErrors = validateForm(formData, {
				compact,
				requiresFiles: compact,
				files: {
					...files,
					[name]: nextFile,
				},
			});

			return {
				...current,
				[name]: nextErrors[name],
			};
		});
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		const nextErrors = validateForm(formData, {
			compact,
			requiresFiles: compact,
			files,
		});
		setErrors(nextErrors);
		setStatus({ type: 'idle', message: '' });

		if (Object.keys(nextErrors).length > 0) {
			return;
		}

		if (!isCaptchaReady) {
			setCaptchaError('reCAPTCHA is still loading. Please try again in a moment.');
			return;
		}

		if (RECAPTCHA_TYPE !== 'v3' && !captchaToken) {
			setCaptchaError('Please complete the reCAPTCHA before sending your message.');
			return;
		}

		setCaptchaError('');
		setIsSubmitting(true);

		try {
			const resolvedCaptchaToken =
				RECAPTCHA_TYPE === 'v3'
					? await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION })
					: captchaToken;

			const response = compact
				? await fetch(CONTACT_API_URL, {
					method: 'POST',
					body: (() => {
						const requestFormData = new FormData();
						requestFormData.append('name', formData.name);
						requestFormData.append('email', formData.email);
						requestFormData.append('phone', formData.phone);
						requestFormData.append('serviceType', formData.serviceType);
						requestFormData.append('message', formData.message);
						requestFormData.append('captchaAction', RECAPTCHA_ACTION);
						requestFormData.append('captchaToken', resolvedCaptchaToken);
						requestFormData.append('nicFront', files.nicFront);
						requestFormData.append('nicBack', files.nicBack);
						return requestFormData;
					})(),
				})
				: await fetch(CONTACT_API_URL, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						...formData,
						captchaAction: RECAPTCHA_ACTION,
						captchaToken: resolvedCaptchaToken,
					}),
				});

			const payload = await response.json().catch(() => ({}));

			if (!response.ok) {
				throw new Error(payload.message || 'Unable to submit your enquiry right now.');
			}

			setFormData(initialFormData);
			setFiles({ nicFront: null, nicBack: null });
			setErrors({});
			setStatus({ type: 'success', message: payload.message || 'Your enquiry has been sent.' });
			setCaptchaToken('');

			if (RECAPTCHA_TYPE !== 'v3' && window.grecaptcha && captchaWidgetIdRef.current !== null) {
				window.grecaptcha.reset(captchaWidgetIdRef.current);
			}
		} catch (error) {
			setStatus({
				type: 'error',
				message: error.message || 'Unable to submit your enquiry right now.',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<section className={`contact-card contact-card--form ${className}`.trim()}>
			{title ? <h2>{title}</h2> : null}
			<form className={`contact-form ${compact ? 'contact-form--compact' : ''}`.trim()} noValidate onSubmit={handleSubmit}>
				<div className="contact-form__field">
					{compact ? (
						<>
							<input type="hidden" name="serviceType" value={formData.serviceType} />
						</>
					) : null}
				</div>
				<div className="contact-form__field">
					<label htmlFor="contact-name">
						Name
						<input
							id="contact-name"
							type="text"
							name="name"
							autoComplete="name"
							value={formData.name}
							onChange={handleChange}
							onBlur={handleBlur}
							aria-invalid={Boolean(errors.name)}
							aria-describedby={errors.name ? 'contact-name-error' : undefined}
						/>
					</label>
					{errors.name ? <p className="contact-form__error" id="contact-name-error">{errors.name}</p> : null}
				</div>
				<div className="contact-form__field">
					<label htmlFor="contact-email">
						Email
						<input
							id="contact-email"
							type="email"
							name="email"
							autoComplete="email"
							value={formData.email}
							onChange={handleChange}
							onBlur={handleBlur}
							aria-invalid={Boolean(errors.email)}
							aria-describedby={errors.email ? 'contact-email-error' : undefined}
						/>
					</label>
					{errors.email ? <p className="contact-form__error" id="contact-email-error">{errors.email}</p> : null}
				</div>
				<div className="contact-form__field">
					<label htmlFor="contact-phone">
						Phone
						<input
							id="contact-phone"
							type="tel"
							name="phone"
							autoComplete="tel"
							value={formData.phone}
							onChange={handleChange}
							onBlur={handleBlur}
							aria-invalid={Boolean(errors.phone)}
							aria-describedby={errors.phone ? 'contact-phone-error' : undefined}
						/>
					</label>
					{errors.phone ? <p className="contact-form__error" id="contact-phone-error">{errors.phone}</p> : null}
				</div>
				{compact ? (
					<>
						<input type="hidden" name="message" value={formData.message} />
						<div className="contact-form__upload-group">
							<p className="contact-form__upload-hint">Accepts JPG, PNG, and PDF files only. Executables and scripts are blocked.</p>
							<div className="contact-form__field">
								<label htmlFor="contact-nic-front">
									Upload NIC front copy
									<input
										id="contact-nic-front"
										type="file"
										name="nicFront"
										accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
										onChange={handleFileChange}
										aria-invalid={Boolean(errors.nicFront)}
										aria-describedby={errors.nicFront ? 'contact-nic-front-error' : undefined}
									/>
								</label>
								{errors.nicFront ? <p className="contact-form__error" id="contact-nic-front-error">{errors.nicFront}</p> : null}
							</div>
							<div className="contact-form__field">
								<label htmlFor="contact-nic-back">
									Upload NIC back copy
									<input
										id="contact-nic-back"
										type="file"
										name="nicBack"
										accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
										onChange={handleFileChange}
										aria-invalid={Boolean(errors.nicBack)}
										aria-describedby={errors.nicBack ? 'contact-nic-back-error' : undefined}
									/>
								</label>
								{errors.nicBack ? <p className="contact-form__error" id="contact-nic-back-error">{errors.nicBack}</p> : null}
							</div>
						</div>
					</>
				) : (
					<div className="contact-form__field">
						<label htmlFor="contact-message">
							Message
							<textarea
								id="contact-message"
								name="message"
								rows="5"
								value={formData.message}
								onChange={handleChange}
								onBlur={handleBlur}
								aria-invalid={Boolean(errors.message)}
								aria-describedby={errors.message ? 'contact-message-error' : undefined}
							/>
						</label>
						{errors.message ? <p className="contact-form__error" id="contact-message-error">{errors.message}</p> : null}
					</div>
				)}
				<div className="contact-form__captcha">
					{RECAPTCHA_TYPE === 'v3' ? (
						<p className="contact-form__hint">Protected by Google reCAPTCHA.</p>
					) : (
						<div ref={captchaContainerRef} />
					)}
					{!isCaptchaReady && RECAPTCHA_SITE_KEY ? (
						<p className="contact-form__hint">Loading reCAPTCHA…</p>
					) : null}
					{captchaError ? <p className="contact-form__error">{captchaError}</p> : null}
				</div>
				{status.message ? (
					<p className={`contact-form__status contact-form__status--${status.type}`} role="status">
						{status.message}
					</p>
				) : null}
				<div className={`contact-form__actions ${compact ? 'contact-form__actions--sticky' : ''}`.trim()}>
					<button type="submit" disabled={isSubmitting || !RECAPTCHA_SITE_KEY || !isCaptchaReady}>
						{isSubmitting ? 'Sending…' : 'Submit Enquiry'}
					</button>
				</div>
			</form>
		</section>
	);
}